import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { offlineManager, useOfflineStatus } from "../lib/offlineManager";

const OfflineStatusIndicator = () => {
  const { isOffline, syncStats } = useOfflineStatus();
  const [isVisible, setIsVisible] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // État pour le drag & drop
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(() => {
    // Récupérer la position sauvegardée ou utiliser la position par défaut
    const saved = localStorage.getItem('offlineIndicatorPosition');
    return saved ? JSON.parse(saved) : { top: 'auto', left: 'auto', bottom: 80, right: 16 };
  });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Sauvegarder la position quand elle change
  useEffect(() => {
    localStorage.setItem('offlineIndicatorPosition', JSON.stringify(position));
  }, [position]);
  
  // Gestionnaires de drag & drop
  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX, y: clientY });
    
    // Empêcher la sélection de texte pendant le drag
    e.preventDefault();
  };
  
  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    
    const deltaX = clientX - dragStart.x;
    const deltaY = clientY - dragStart.y;
    
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };
    
    // Convertir position actuelle en left/top si elle est en right/bottom
    let currentLeft = position.left !== 'auto' ? position.left : viewport.width - position.right - 200;
    let currentTop = position.top !== 'auto' ? position.top : viewport.height - position.bottom - 80;
    
    // CORRECTION FINALE : Inverser deltaX pour mouvement naturel
    let newLeft = currentLeft - deltaX; // - pour que glisser à droite aille à droite
    let newTop = currentTop + deltaY;   // + pour aller vers le bas, - pour aller vers le haut
    
    // Limiter aux bords de l'écran
    newLeft = Math.max(16, Math.min(newLeft, viewport.width - 200));
    newTop = Math.max(16, Math.min(newTop, viewport.height - 80));
    
    setPosition({
      top: newTop,
      left: newLeft,
      bottom: 'auto',
      right: 'auto'
    });
    
    setDragStart({ x: clientX, y: clientY });
    e.preventDefault();
  };
  
  const handleDragEnd = () => {
    setIsDragging(false);
  };
  
  // Ajouter les listeners globaux pour le drag
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();
      const handleTouchMove = (e) => handleDragMove(e);
      const handleTouchEnd = () => handleDragEnd();
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
    // eslint-disable-next-line -- les données de handleDragMove (dragStart, position) sont déjà dans les dépendances
  }, [isDragging, dragStart, position]);
  
  // Gérer la visibilité intelligente
  useEffect(() => {
    const shouldShow = isOffline || syncStats.pending > 0 || syncStats.failed > 0;
    setIsVisible(shouldShow);
    
    // Auto-masquer après synchronisation réussie
    if (!isOffline && syncStats.pending === 0 && syncStats.failed === 0) {
      const timer = setTimeout(() => setIsVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOffline, syncStats]);
  
  // Mettre à jour le temps de dernière sync
  useEffect(() => {
    if (syncStats.lastSync) {
      setLastSyncTime(new Date(syncStats.lastSync));
    }
  }, [syncStats.lastSync]);
  
  if (!isVisible) return null;
  
  // Déterminer l'état et les couleurs
  const getStatusConfig = () => {
    if (isOffline) {
      return {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-300',
        dotColor: 'bg-yellow-500',
        icon: '🔌',
        title: 'Mode hors ligne',
        subtitle: 'Les données seront synchronisées au retour de la connexion'
      };
    } else if (syncStats.pending > 0) {
      return {
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        borderColor: 'border-blue-300',
        dotColor: 'bg-blue-500',
        icon: '🔄',
        title: 'Synchronisation...',
        subtitle: `${syncStats.pending} éléments en attente`
      };
    } else if (syncStats.failed > 0) {
      return {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        borderColor: 'border-red-300',
        dotColor: 'bg-red-500',
        icon: '⚠️',
        title: 'Erreurs de synchronisation',
        subtitle: `${syncStats.failed} éléments échoués`
      };
    } else {
      return {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        borderColor: 'border-green-300',
        dotColor: 'bg-green-500',
        icon: '✅',
        title: 'Synchronisé',
        subtitle: lastSyncTime ? `Dernière sync: ${lastSyncTime.toLocaleTimeString()}` : ''
      };
    }
  };
  
  const config = getStatusConfig();
  
  return (
    <div 
      className={`fixed z-50 px-3 py-2 rounded-lg shadow-lg transition-all duration-300 max-w-xs cursor-move select-none ${
        config.bgColor
      } ${config.textColor} ${config.borderColor} border ${isDragging ? 'opacity-75' : ''}`}
      style={{
        bottom: position.bottom,
        right: position.right,
        top: position.top,
        left: position.left
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      title="Glissez pour déplacer l'indicateur"
    >
      <div className="flex items-center space-x-2 text-xs">
        <span className="text-sm pointer-events-none">{config.icon}</span>
        <div className="flex items-center space-x-1 pointer-events-none">
          <span className={`w-1.5 h-1.5 ${config.dotColor} rounded-full ${isOffline || syncStats.pending > 0 ? 'animate-pulse' : ''}`}></span>
          <span className="font-medium">{config.title}</span>
        </div>
        {(syncStats.pending > 0 || syncStats.failed > 0) && (
          <span className="text-xs bg-white rounded px-1.5 py-0.5 font-medium pointer-events-none">
            {syncStats.pending > 0 && `${syncStats.pending}`}
            {syncStats.pending > 0 && syncStats.failed > 0 && ' • '}
            {syncStats.failed > 0 && `${syncStats.failed}`}
          </span>
        )}
      </div>
      {config.subtitle && (
        <div className="text-xs mt-1 opacity-75 pointer-events-none">
          {config.subtitle}
        </div>
      )}
      
      {/* Bouton de synchronisation manuelle si nécessaire */}
      {(syncStats.failed > 0 || (isOffline === false && syncStats.pending > 0)) && (
        <div className="flex flex-col gap-1 mt-2" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
          <button
            data-testid="offline-retry-sync-button"
            onClick={() => {
              offlineManager.triggerSync();
              toast.info('🔄 Synchronisation manuelle déclenchée');
            }}
            className="text-xs px-2 py-1 bg-white rounded hover:bg-gray-50 transition-colors"
          >
            Réessayer maintenant
          </button>
          {syncStats.failed > 0 && (
            <button
              data-testid="offline-reset-queue-button"
              onClick={async () => {
                const count = await offlineManager.clearSyncQueue();
                toast.success(`🧹 File réinitialisée (${count} élément(s) supprimé(s))`);
              }}
              className="text-xs px-2 py-1 bg-white/80 rounded hover:bg-gray-50 transition-colors text-red-700 border border-red-200"
            >
              Réinitialiser la file
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Composant principal App avec gestion hors ligne optimisée

export default OfflineStatusIndicator;
